import React from "react";
import { CONTRACTS_TABLE_COLUMNS } from "constants/translates";
import TextInput from "components/tables/logisticTable/columns/TextInput";
import DatePicker from "components/tables/logisticTable/columns/DatePicker";
import moment from "moment";
import AutoComplete from "components/tables/logisticTable/columns/AutoComplete";
import TextArea from "components/tables/logisticTable/columns/TextArea";
import Select from "components/tables/logisticTable/columns/SelectField";
import Checkbox from "components/tables/logisticTable/columns/Checkbox";
import UploadFile from "components/tables/logisticTable/columns/UploadFile";
import Actions from "./columns/actions";
import { Column } from "../../common/Table/Interfaces";
import { Option } from "components/common/forms/Interfaces";
import SearchFilter from "components/tables/declarantTable/filters/searchFilter";
import DateTimeFilter from "components/tables/declarantTable/filters/dateTimeFilter";
import SelectFilter from "components/tables/declarantTable/filters/selectFilter";
import { AttachedFile } from "components/tables/logisticTable/columns/AttachedFile";
import { Contracts } from "models/Contracts";
import ContractsStore from "stores/ContractsStore";
import {
  DELETE_CONTRACTS_FILE,
  DOWNLOAD_CONTRACTS_FILE,
} from "constants/events";
import { ROLE_ADMIN } from "constants/authorize";
import UserStore from "stores/UserStore";

const BAN_ON_REGISTRATION_FILTER_OPTIONS = [
  { value: "true", title: "Yes" },
  { value: "false", title: "No" },
] as Option[];

const requiredFieldValidation = {
  validationRules: [
    {
      required: true,
      message: "Required field",
    },
  ],
};

const COLUMNS = (form: any, userStore: UserStore): Column[] => {
  const {
    editingKeys,
    countryOptions,
    companyNameOptions,
    statusOptions,
  } = ContractsStore;

  const countriesFilterOptions = countryOptions?.map((country) => ({
    title: country?.label,
    value: country?.value,
  }));

  const statusFilterOptions = statusOptions?.map((status) => ({
    title: status?.label,
    value: status?.value,
  }));

  const setFieldValue = (fieldName: string, entity: Contracts, value: any) => {
    const stateValues = form.getFieldsValue();

    if (stateValues?.[`${fieldName}_${entity.id}`] === undefined) {
      form.setFieldsValue({
        [`${fieldName}_${entity.id}`]: value,
      });
    }
  };

  return [
    {
      title: CONTRACTS_TABLE_COLUMNS.INDEX,
      dataIndex: "id",
      key: "id",
      sorter: false,
      isActive: true,
      order: 1,
      width: 60,
      editable: false,
      render: (text, entity: Contracts, index) => {
        return index + 1;
      },
    },
    {
      title: CONTRACTS_TABLE_COLUMNS.companyName,
      dataIndex: "company_name",
      key: "company_name",
      sorter: true,
      order: 2,
      width: 150,
      ellipsis: true,
      isActive: true,
      editable: true,
      inputType: "text",
      filterDropdown: SearchFilter,
      render: (text, entity: Contracts) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("company_name", entity, entity.company_name);

          return (
            <AutoComplete
              defaultValue={text}
              name={`company_name_${entity.id}`}
              defaultOptions={companyNameOptions}
            />
          );
        }

        return entity.company_name;
      },
    },
    {
      title: CONTRACTS_TABLE_COLUMNS.createdDate,
      dataIndex: "created_date",
      key: "created_date",
      sorter: true,
      order: 3,
      width: 150,
      ellipsis: true,
      isActive: true,
      editable: false,
      filterDropdown: DateTimeFilter,
      render: (text, entity: Contracts) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("created_date", entity, moment(entity.created_date));

          return (
            <DatePicker
              value={moment(text)}
              name={`created_date_${entity.id}`}
              config={requiredFieldValidation}
            />
          );
        }

        return entity.created_date;
      },
    },
    {
      title: CONTRACTS_TABLE_COLUMNS.country,
      dataIndex: "country",
      key: "country",
      sorter: true,
      order: 4,
      width: 150,
      ellipsis: true,
      isActive: true,
      editable: false,
      render: (text, entity: Contracts) => {
        const countryDefaultValue = countryOptions.find(
          (option) => option.value === entity.country
        );

        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("country", entity, countryDefaultValue?.value);

          return (
            <Select
              defaultValue={entity.country}
              name={`country_${entity.id}`}
              options={countryOptions}
            />
          );
        }

        return countryDefaultValue?.label;
      },
      filterDropdown: ({
        setSelectedKeys,
        confirm,
        clearFilters,
        selectedKeys,
      }) => (
        <SelectFilter
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          options={countriesFilterOptions}
          selectedKeys={selectedKeys}
        />
      ),
    },
    {
      title: CONTRACTS_TABLE_COLUMNS.unp,
      dataIndex: "unp",
      key: "unp",
      sorter: true,
      order: 5,
      width: 150,
      ellipsis: true,
      isActive: true,
      editable: true,
      inputType: "text",
      filterDropdown: SearchFilter,
      render: (text, entity: Contracts) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("unp", entity, entity.unp);

          return <TextInput value={text} name={`unp_${entity.id}`} />;
        }

        return entity.unp;
      },
    },
    {
      title: CONTRACTS_TABLE_COLUMNS.contract,
      dataIndex: "contract",
      key: "contract",
      sorter: true,
      order: 5,
      width: 150,
      ellipsis: true,
      isActive: true,
      editable: true,
      inputType: "text",
      filterDropdown: SearchFilter,
      render: (text, entity: Contracts) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          const asAdmin = userStore?.authorizeByRole(ROLE_ADMIN);
          const isEditing = !!entity.contract;
          const activeField = asAdmin && isEditing;
          setFieldValue("contract", entity, entity.contract);

          return (
            <TextInput
              value={text}
              name={`contract_${entity.id}`}
              disabled={!activeField}
            />
          );
        }

        return entity.contract;
      },
    },
    {
      title: CONTRACTS_TABLE_COLUMNS.note,
      dataIndex: "note",
      key: "note",
      sorter: true,
      order: 6,
      width: 200,
      ellipsis: true,
      isActive: true,
      editable: false,
      filterDropdown: SearchFilter,
      render: (text, entity: Contracts) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("note", entity, entity.note);

          return <TextArea value={text} name={`note_${entity.id}`} />;
        }

        return entity.note;
      },
    },
    {
      title: CONTRACTS_TABLE_COLUMNS.status,
      dataIndex: "status",
      key: "status",
      sorter: true,
      order: 7,
      width: 150,
      ellipsis: true,
      isActive: true,
      editable: false,
      filterDropdown: ({
        setSelectedKeys,
        confirm,
        clearFilters,
        selectedKeys,
      }) => (
        <SelectFilter
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          options={statusFilterOptions}
          selectedKeys={selectedKeys}
        />
      ),
      render: (text, entity: Contracts) => {
        const defaultValue = statusOptions.find(
          (option) => option.value === entity.status
        );

        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("status", entity, defaultValue?.value);

          return (
            <Select
              defaultValue={text}
              name={`status_${entity.id}`}
              options={statusOptions}
            />
          );
        }

        return defaultValue?.label;
      },
    },
    {
      title: CONTRACTS_TABLE_COLUMNS.banOnRegistration,
      dataIndex: "ban_on_registration",
      key: "ban_on_registration",
      sorter: true,
      order: 8,
      width: 120,
      ellipsis: true,
      isActive: true,
      editable: false,
      render: (text, entity: Contracts) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue(
            "ban_on_registration",
            entity,
            Boolean(entity.ban_on_registration)
          );
        }

        return (
          <Checkbox
            defaultChecked={Boolean(text)}
            name={
              editMode
                ? `ban_on_registration_${entity.id}`
                : `ban_on_registration_view_${entity.id}`
            }
            disabled={!editMode}
            form={form}
          />
        );
      },
      filterDropdown: ({
        setSelectedKeys,
        confirm,
        clearFilters,
        selectedKeys,
      }) => {
        return (
          <SelectFilter
            setSelectedKeys={setSelectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            options={BAN_ON_REGISTRATION_FILTER_OPTIONS}
            selectedKeys={selectedKeys}
          />
        );
      },
    },
    {
      title: CONTRACTS_TABLE_COLUMNS.documents,
      dataIndex: "documents",
      key: "documents",
      sorter: true,
      order: 9,
      width: 400,
      ellipsis: true,
      isActive: true,
      editable: false,
      render: (text, entity: Contracts) => {
        const editMode = editingKeys?.some((key) => key === entity.id);

        if (editMode) {
          setFieldValue("documents", entity, entity.documents);

          return (
            <>
              <AttachedFile
                documents={entity.documents}
                id={entity.id}
                downloadEventId={DOWNLOAD_CONTRACTS_FILE}
                deleteEventId={DELETE_CONTRACTS_FILE}
                deleteFile
              />
              <UploadFile defaultValue={text} name={`documents_${entity.id}`} />
            </>
          );
        }

        return (
          <AttachedFile
            documents={entity.documents}
            id={entity.id}
            downloadEventId={DOWNLOAD_CONTRACTS_FILE}
            deleteEventId={DELETE_CONTRACTS_FILE}
          />
        );
      },
    },
    {
      title: CONTRACTS_TABLE_COLUMNS.actions,
      dataIndex: "actions",
      key: "actions",
      sorter: false,
      order: 9999,
      width: 180,
      ellipsis: true,
      render: (text, entity: Contracts) => {
        return (
          <Actions entity={entity} isTemporary={entity.temporary} form={form} />
        );
      },
      isActive: true,
    },
  ] as Column[];
};

export default COLUMNS;
